import styled, { css, FlattenSimpleInterpolation } from "styled-components";
import { FC, useEffect, useRef } from "react";

import { BaseComponentProps } from "../types";
import { primaryActive, primaryInactive } from "../../typography/alpha";
import { FontWeight, ItemSize } from "../../types/components";
import { getFontSize } from "../../utils/components";

export enum CheckboxTestIds {
  Container = "checkbox",
}

const Input = styled.input`
  position: absolute;
  height: 0;
  width: 0;
  opacity: 0;
  z-index: -1;
`;

const getFontShape = ($indeterminate?: boolean, $size?: ItemSize) => {
  if ($indeterminate) {
    return css`
      font-size: ${$size === "medium" || $size === "large" ? 24 : 16}px;
      font-weight: var(--font-weight-extrabold);
    `;
  }
  return css`
    font-size: ${$size === "medium" || $size === "large" ? 14 : 9}px;
    font-weight: var(--font-weight-medium);
  `;
};

type MarkerProps = {
  $size?: ItemSize;
  $checked?: boolean;
  $indeterminate?: boolean;
};

const Marker = styled.div<MarkerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }): number =>
    $size === "medium" || $size === "large" ? 24 : 16}px;
  height: ${({ $size }): number =>
    $size === "medium" || $size === "large" ? 24 : 16}px;
  border: 1px solid var(--color-primary-400);
  border-radius: 2px;
  box-sizing: border-box;

  &::after {
    content: "";
    color: white;
    ${({ $size, $indeterminate }): FlattenSimpleInterpolation =>
      getFontShape($indeterminate, $size)}
  }

  ${Input}:checked + &, ${Input}:indeterminate + & {
    border-color: var(--color-secondary-800);
    background-color: var(--color-secondary-800);
  }

  ${Input}:checked + &::after {
    content: "✔";
  }

  ${Input}:indeterminate + &::after {
    content: "﹣";
  }

  ${Input}:disabled + & {
    border-color: var(--color-primary-200);
  }
`;

type LabelProps = {
  $disabled?: boolean;
  $size: ItemSize;
  $fontWeight: FontWeight;
};

const Label = styled.label<LabelProps>`
  display: grid;
  grid-template-columns: min-content max-content;
  grid-template-areas: "marker label";
  align-items: center;
  width: max-content;
  ${({ $disabled }) =>
    $disabled ? primaryInactive.subtitle3 : primaryActive.subtitle3};
  cursor: ${({ $disabled }): string => ($disabled ? "not-allowed" : "pointer")};
  font-size: ${({ $size }): number => getFontSize($size)}px;
  font-weight: var(
    --font-weight-${({ $fontWeight }): FontWeight => $fontWeight}
  );

  span {
    grid-area: label;
    user-select: none;
    margin-left: 4px;
  }

  ${Marker} {
    grid-area: marker;
  }
`;

type Props = Omit<BaseComponentProps<"input">, "size"> & {
  id: string;
  testId?: string;
  className?: string;
  label?: string;
  size?: ItemSize;
  fontWeight?: FontWeight;
  indeterminate?: boolean;
};

export const Checkbox: FC<Props> = ({
  id,
  testId,
  className,
  label,
  size = "regular" as ItemSize,
  fontWeight = "regular" as FontWeight,
  disabled,
  indeterminate,
  ...args
}) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref?.current) {
      ref.current.indeterminate = Boolean(indeterminate);
      if (ref.current.indeterminate) {
        ref.current.checked = false;
      }
    }
  }, [indeterminate]);

  return (
    <Label
      htmlFor={id}
      data-testid={testId ?? CheckboxTestIds.Container}
      $disabled={disabled}
      $size={size}
      $fontWeight={fontWeight}
      className={className}
    >
      {Boolean(label) && <span>{label}</span>}
      <Input
        aria-label={label}
        ref={ref}
        id={id}
        type="checkbox"
        {...args}
        disabled={disabled}
      />
      <Marker
        $size={size}
        $indeterminate={indeterminate}
        $checked={ref?.current?.checked}
      />
    </Label>
  );
};
